import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CancelRounded, CheckCircle } from '@mui/icons-material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import PageContainer from '../PageContainer';
import { USERS_LIST_ROWS_PER_PAGE, VISIBLE_PAGE_COUNT } from '../../constants';
import { useAppUsersQuery } from '../../api/use-app-users-query';
import { useBanAppUserMutation } from '../../api/use-ban-app-user-mutation';
import { formatAppUserJoinedAt } from '../../utils/usersModel';
import { getUserInitial } from '../../utils/userDisplay';
import UserGenderBadge from '../shared/UserGenderBadge';
import PortalPaginationControls from '../layout/PortalPaginationControls';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../styles/portalListTable';

const SEARCH_DEBOUNCE_MS = 400;

const searchFieldWrapSx = {
  position: 'relative',
  width: '100%',
  maxWidth: 280,
  mb: 2,
};

const searchFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: 'background.paper',
    fontSize: '0.875rem',
    pr: 4.5,
  },
};

const searchSpinnerSx = {
  position: 'absolute',
  right: 12,
  top: '50%',
  mt: '-8px',
  pointerEvents: 'none',
};

const BooleanStatusIcon = ({ value, trueLabel, falseLabel }) =>
  value ? (
    <CheckCircle titleAccess={trueLabel} sx={{ fontSize: 20, color: 'success.main' }} />
  ) : (
    <CancelRounded titleAccess={falseLabel} sx={{ fontSize: 20, color: 'text.disabled' }} />
  );

const UserTextCell = ({ value }) => (
  <Typography variant="body2">{value?.trim() ? value : '—'}</Typography>
);

const bannedRowSx = {
  opacity: 0.62,
  bgcolor: 'action.hover',
  '& td': {
    color: 'text.disabled',
  },
  '& .MuiAvatar-root': {
    opacity: 0.75,
  },
};

/** Admin-only list of all platform users. */
const Users = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userToBan, setUserToBan] = useState(null);
  const banAppUserMutation = useBanAppUserMutation();

  const limit = USERS_LIST_ROWS_PER_PAGE;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const queryParams = useMemo(() => {
    const params = { page, limit };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    return params;
  }, [limit, page, debouncedSearch]);

  const { data, isLoading, isFetching, error } = useAppUsersQuery(queryParams);
  const isRefetching = isFetching && !isLoading;

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(page, totalPages);

  const handleOpenBanDialog = (user) => {
    if (user.isBanned) return;
    setUserToBan(user);
  };

  const handleCloseBanDialog = () => {
    if (banAppUserMutation.isPending) return;
    setUserToBan(null);
  };

  const handleConfirmBan = async () => {
    if (!userToBan?.id) return;

    try {
      const result = await banAppUserMutation.mutateAsync(userToBan.id);
      toast.success(result.message || 'User banned successfully');
      setUserToBan(null);
    } catch (banError) {
      toast.error(banError.message || 'Failed to ban user');
    }
  };

  return (
    <PageContainer
      title="Users"
      contentSx={{
        pb: { xs: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box sx={searchFieldWrapSx}>
        <TextField
          size="small"
          placeholder="Search users"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={searchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        {isRefetching ? <CircularProgress size={16} color="inherit" sx={searchSpinnerSx} /> : null}
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      {!isLoading && !error && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                opacity: isRefetching ? 0.6 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              <Table stickyHeader>
                <PortalTableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Joined At</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Profession</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell align="center">Profile Verified</TableCell>
                    <TableCell align="center">Onboarding Completed</TableCell>
                    <TableCell>Interests</TableCell>
                    <TableCell align="center">Banned</TableCell>
                  </TableRow>
                </PortalTableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <PortalTableRow>
                      <PortalTableCell colSpan={11}>
                        <Typography variant="body2" color="text.secondary">
                          No users found.
                        </Typography>
                      </PortalTableCell>
                    </PortalTableRow>
                  ) : (
                    users.map((user) => (
                      <PortalTableRow
                        key={user.id || user.userEmail}
                        sx={user.isBanned ? bannedRowSx : undefined}
                      >
                        <PortalTableCell>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
                          >
                            <Avatar src={user.avatarUrl || undefined} alt={user.fullName}>
                              {getUserInitial(user.fullName, user.userEmail)}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{
                                minWidth: 0,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.fullName || '—'}
                            </Typography>
                          </Box>
                        </PortalTableCell>
                        <PortalTableCell>
                          <Typography variant="body2">
                            {formatAppUserJoinedAt(user.createdAt)}
                          </Typography>
                        </PortalTableCell>
                        <PortalTableCell>
                          <UserTextCell value={user.userEmail} />
                        </PortalTableCell>
                        <PortalTableCell>
                          <UserTextCell value={user.phoneNumber} />
                        </PortalTableCell>
                        <PortalTableCell>
                          <UserTextCell value={user.profession} />
                        </PortalTableCell>
                        <PortalTableCell>
                          <UserTextCell value={user.location} />
                        </PortalTableCell>
                        <PortalTableCell>
                          <UserGenderBadge gender={user.gender} />
                        </PortalTableCell>
                        <PortalTableCell align="center">
                          <BooleanStatusIcon
                            value={user.profileVerified}
                            trueLabel="Profile verified"
                            falseLabel="Profile not verified"
                          />
                        </PortalTableCell>
                        <PortalTableCell align="center">
                          <BooleanStatusIcon
                            value={user.onboardingCompleted}
                            trueLabel="Onboarding completed"
                            falseLabel="Onboarding not completed"
                          />
                        </PortalTableCell>
                        <PortalTableCell sx={{ whiteSpace: 'normal' }}>
                          {user.interests?.length ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {user.interests.map((interest) => (
                                <Chip
                                  key={interest.id}
                                  label={interest.label}
                                  title={interest.category || undefined}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </PortalTableCell>
                        <PortalTableCell align="center">
                          {user.isBanned ? (
                            <Tooltip title="Banned">
                              <GppBadOutlinedIcon sx={{ fontSize: 20, color: 'error.main' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Ban user">
                              <IconButton
                                aria-label={`Ban ${user.fullName || user.userEmail || 'user'}`}
                                size="small"
                                onClick={() => handleOpenBanDialog(user)}
                                disabled={banAppUserMutation.isPending}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'error.main',
                                    bgcolor: 'error.light',
                                  },
                                }}
                              >
                                <BlockOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </PortalTableCell>
                      </PortalTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {total > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderTopColor: 'divider',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <PortalPaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  visibleCount={VISIBLE_PAGE_COUNT}
                  isLoading={isFetching}
                  onPageChange={setPage}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      <Dialog
        open={Boolean(userToBan)}
        onClose={handleCloseBanDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            maxWidth: 420,
            width: '100%',
          },
        }}
      >
        <DialogContent sx={{ pb: 1, pt: 1 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <BlockOutlinedIcon sx={{ fontSize: 30, color: 'error.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Ban this user?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Are you sure you want to ban{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {userToBan?.fullName || userToBan?.userEmail || 'this user'}
            </Box>
            ? They will lose access to the platform.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 1, pt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseBanDialog}
            disabled={banAppUserMutation.isPending}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmBan}
            disabled={banAppUserMutation.isPending}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            {banAppUserMutation.isPending ? 'Banning…' : 'Ban user'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Users;
